import {
	API_ENDPOINTS,
	DataTable,
	DateFormats,
	formatDateFns,
	FormInput,
	Icon,
	Icons,
	ImageType,
	PageLayout,
	Permissions,
	route,
	StringHelpers,
	useAuthorisationContext,
	useLangContext,
	Variants,
	DataColumnInterface,
	replaceKeyWithValue,
	FormInputType,
} from 'carrier-fe';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ClaimListItem } from '../../types/claim';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import { useMemo } from 'react';

function ClaimsList() {
	// Hooks and Contexts
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { hasPermissionAccess } = useAuthorisationContext();
	const { crud, fields } = useLangContext();

	// Handler functions
	const handleOptionSelect = (
		action: string | ImageType | null,
		data: ClaimListItem
	) => {
		const { id = '' } = data;
		const actions: { [key: string]: () => void } = {
			view: () => {
				navigate(`/claims/${id}`);
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	// Column mapping function
	const colMapping = (data: ClaimListItem): DataColumnInterface[] => {
		const addressDisplay = [
			data?.address_line_1,
			data?.town_city,
			data?.postcode_zipcode,
		]
			.filter(Boolean)
			.join(', ') || '-';

		return [
			{
				id: 'customer_name',
				label: StringHelpers.title(
					fields?.customer_name || 'Customer'
				),
				value: data?.customer_name || '-',
				sortable: true,
			},
			{
				id: 'address',
				label: StringHelpers.title(
					fields?.address || 'Address'
				),
				value: addressDisplay,
				sortable: false,
			},
			{
				id: 'created_at',
				label: StringHelpers.title(
					fields?.created_at || 'Claim Date'
				),
				value: formatDateFns(data?.created_at, DateFormats.DATETIME_SHORT) || '-',
				sortable: true,
			},
			{
				id: 'status',
				label: StringHelpers.title(
					fields?.status || 'Status'
				),
				value: data?.status_display || '-',
			},
			{
				id: 'deleted_at',
				label: StringHelpers.title(
					fields?.archived_at || 'Archived'
				),
				value: formatDateFns(data?.deleted_at, DateFormats.DATETIME_SHORT) || '-',
				sortable: true,
				visibleCondition: () => !!searchParams.get('filter[trashed]'),
			},
			{
				id: 'actions',
				label: StringHelpers.title(
					fields?.actions || 'Actions'
				),
				value: data.permissions?.view ? (
					<FormInput
						type='select'
						label={StringHelpers.title(
							fields?.actions || 'Action'
						)}
						placeholder={StringHelpers.title(
							fields?.select || 'Select'
						)}
						name='actions'
						value=''
						onChange={(value: any) => {
							handleOptionSelect(value, data);
						}}
						options={[
							{
								label: StringHelpers.title(
									crud?.view || 'View'
								),
								value: 'view',
							},
						]}
						noMargin
					/>
				) : null,
			},
		];
	};

	// Dropdown options for page actions
	const dropDownOptions = (): DropdownItemProps[] => {
		if (!hasPermissionAccess(Permissions.TOSHIBA_CLAIM_STORE)) {
			return [];
		}

		return [
			{
				label: StringHelpers.title(
					crud?.buttons?.create?.default || 'Create'
				),
				icon: <Icon icon={Icons.ADD} />,
				onClick: () => {
					navigate('/claims/create');
				},
			},
		];
	};

	const title = useMemo(
		() => StringHelpers.title(
			replaceKeyWithValue(
				crud?.pages?.index?.title || ':model List ',
				'model',
				crud?.models?.claim || 'Claim'
			)
		),
		[crud]
	);

	const filters: FormInputType[] = [
		{
			name: 'status',
			label: StringHelpers.title(fields?.status),
			type: 'searchable-select',
			value: '',
			placeholder: StringHelpers.title(
                fields?.please_select 
                || 'please select'
            ),
			options: crud?.options?.claim_status,
		}
	];

	return (
		<PageLayout
			title={title}
		>
			<DataTable<ClaimListItem>
				routeEndpoint={route(API_ENDPOINTS.REGISTRATION.CLAIM.INDEX)}
				colMapping={colMapping}
				incrementPaginationButtons
				variant={Variants.Info}
				items={dropDownOptions()}
				filters={filters}
			/>
		</PageLayout>
	);
}

export default ClaimsList;
